import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Maximize2, Pause, Play, X } from 'lucide-react';
import { useAmbiance } from '../Ambiance/AmbianceContext';
import type { CaptureMetier } from './vitrineMetiers';

const DUREE_ETAPE_MS = 7000;
const STYLE_VIDEO = `
@keyframes mv-remplir { from { transform: scaleX(0); } to { transform: scaleX(1); } }
@keyframes mv-zoom { from { transform: scale(1); } to { transform: scale(1.035); } }
@keyframes mv-entree { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .mv-anime { animation: none !important; } }
`;

/**
 * Micro-vidéo d'un module : les vraies captures de l'application défilent seules, une étape après l'autre, avec une
 * barre de progression par étape, la légende qui change et un bouton pause. La lecture ne tourne que lorsque le
 * bloc est à l'écran, se met en pause au survol et ne démarre pas si l'appareil demande moins d'animation.
 * Les flèches, les points et le clavier restent disponibles ; un clic sur l'image l'ouvre en grand.
 *
 * Visite guidée d'un module : une vraie capture de l'application dans un cadre de navigateur, des flèches
 * pour passer à la suivante, et le texte qui change avec l'image (titre, puis les points sous forme de cartes).
 * Le cadre garde toujours la même taille, quelle que soit la capture : on ne voit plus de grandes et de petites images
 * se succéder. Un clic sur l'image l'ouvre en grand. Clavier : flèches gauche et droite quand le diaporama a le focus.
 */
export const CaptureCarousel: React.FC<{ captures: CaptureMetier[] }> = ({ captures }) => {
    const { tokens } = useAmbiance();
    const [i, setI] = useState(0);
    const [agrandie, setAgrandie] = useState<string | null>(null);
    const n = captures.length;
    const aller = useCallback((k: number) => setI(((k % n) + n) % n), [n]);
    const sansAnimation = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const [lecture, setLecture] = useState(!sansAnimation);
    const [enVue, setEnVue] = useState(false);
    const [survol, setSurvol] = useState(false);
    const section = useRef<HTMLElement>(null);
    const enMarche = n > 1 && lecture && enVue && !survol && !agrandie;

    useEffect(() => {
        const el = section.current;
        if (!el || typeof IntersectionObserver === 'undefined') { setEnVue(true); return undefined; }
        const obs = new IntersectionObserver(([e]) => setEnVue(e.isIntersecting), { threshold: 0.4 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (!agrandie) return undefined;
        const fermer = (e: KeyboardEvent) => { if (e.key === 'Escape') setAgrandie(null); };
        document.addEventListener('keydown', fermer);
        const avant = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.removeEventListener('keydown', fermer); document.body.style.overflow = avant; };
    }, [agrandie]);

    if (n === 0) return null;
    const c = captures[i];
    const colonnes = c.points.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : c.points.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
    const bouton = 'absolute top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-surface border border-border shadow-lg flex items-center justify-center hover:border-content hover:scale-105 transition-all';

    return (
        <section
            ref={section}
            className="max-w-[1160px] mx-auto px-5 pt-8 pb-14 md:pt-10 md:pb-20"
            aria-roledescription="diaporama"
            aria-label={`Captures : ${c.module}`}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'ArrowRight') aller(i + 1); if (e.key === 'ArrowLeft') aller(i - 1); }}
            onMouseEnter={() => setSurvol(true)}
            onMouseLeave={() => setSurvol(false)}
        >
            <style>{STYLE_VIDEO}</style>
            <div className="relative">
                <div className="rounded-2xl border border-border bg-surface shadow-[0_24px_60px_-20px_rgba(0,0,0,.28)] overflow-hidden">
                    <div className="flex items-center gap-1.5 h-9 px-4 border-b border-border bg-surface-soft" aria-hidden="true">
                        <span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" /><span className="w-2.5 h-2.5 rounded-full bg-border" />
                        <span className="ml-3 text-[11px] font-mono text-content-muted truncate">l-iamani.com · {c.module}</span>
                    </div>
                    {n > 1 && (
                        <div className="flex gap-1.5 px-4 pt-3 bg-surface-soft" aria-hidden="true">
                            {captures.map((x, k) => (
                                <span key={x.src + k} className="relative h-1 flex-1 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                                    {k < i && <span className="absolute inset-0 rounded-full" style={{ background: tokens.acc }} />}
                                    {k === i && (
                                        <span
                                            key={`${i}-${lecture}`}
                                            className="mv-anime absolute inset-0 rounded-full origin-left"
                                            style={{ background: tokens.acc, animation: lecture ? `mv-remplir ${DUREE_ETAPE_MS}ms linear forwards` : undefined, animationPlayState: enMarche ? 'running' : 'paused', transform: lecture ? undefined : 'scaleX(1)' }}
                                            onAnimationEnd={() => { if (lecture) aller(i + 1); }}
                                        />
                                    )}
                                </span>
                            ))}
                        </div>
                    )}
                    {/* Cadre de taille fixe : la capture s'y inscrit en entier, sans être coupée */}
                    <button
                        type="button"
                        onClick={() => setAgrandie(c.duo ? c.duo[0].src : c.src)}
                        className="group relative block w-full h-[320px] sm:h-[420px] md:h-[560px] bg-surface-soft p-3 md:p-5 cursor-zoom-in"
                        aria-label="Agrandir la capture"
                    >
                        {c.duo ? (
                            <span className="grid grid-cols-2 gap-3 h-full">
                                {c.duo.map((d) => (
                                    <span key={d.src} className="flex flex-col min-w-0 min-h-0">
                                        <span className="flex-1 min-h-0 flex items-center justify-center"><img src={d.src} alt={d.alt} className="max-w-full max-h-full object-contain rounded-lg shadow-md border border-border" /></span>
                                        <span className="mt-2 text-center text-[11px] md:text-[13px] font-semibold text-content-muted">{d.legende}</span>
                                    </span>
                                ))}
                            </span>
                        ) : (
                            <span className="flex h-full items-center justify-center"><img key={c.src} src={c.src} alt={c.alt} className="mv-anime max-w-full max-h-full object-contain rounded-lg shadow-md" style={lecture && n > 1 ? { animation: `mv-zoom ${DUREE_ETAPE_MS}ms ease-out forwards`, animationPlayState: enMarche ? 'running' : 'paused' } : undefined} /></span>
                        )}
                        <span className="absolute right-3 bottom-3 inline-flex items-center gap-1.5 rounded-full bg-surface/95 border border-border px-3 py-1.5 text-[12px] font-semibold opacity-90 group-hover:opacity-100"><Maximize2 size={13} aria-hidden="true" />Agrandir</span>
                    </button>
                </div>
                {n > 1 && (
                    <>
                        <button type="button" aria-label="Capture précédente" onClick={() => aller(i - 1)} className={`${bouton} left-2 md:-left-5`}><ChevronLeft size={20} aria-hidden="true" /></button>
                        <button type="button" aria-label="Capture suivante" onClick={() => aller(i + 1)} className={`${bouton} right-2 md:-right-5`}><ChevronRight size={20} aria-hidden="true" /></button>
                    </>
                )}
            </div>

            <div className="mt-7" aria-live="polite">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h3 key={c.titre} className="mv-anime text-[22px] md:text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] max-w-[760px]" style={{ animation: 'mv-entree .4s ease both' }}>{c.titre}</h3>
                    {n > 1 && (
                        <div className="flex items-center gap-3 shrink-0">
                            <button type="button" onClick={() => setLecture((v) => !v)} aria-label={lecture ? 'Mettre la lecture en pause' : 'Lancer la lecture'} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-[12px] font-semibold hover:border-content transition-colors">
                                {lecture ? <Pause size={13} aria-hidden="true" /> : <Play size={13} aria-hidden="true" />}{lecture ? 'Pause' : 'Lecture'}
                            </button>
                            <span className="text-[12.5px] font-semibold text-content-muted tabular-nums">{i + 1} / {n}</span>
                            <div className="flex gap-1.5" role="group" aria-label="Choisir une capture">
                                {captures.map((x, k) => (
                                    <button key={x.src + k} type="button" aria-label={`Capture ${k + 1}`} aria-current={k === i} onClick={() => aller(k)} className="h-6 w-6 flex items-center justify-center">
                                        <span className="block h-2 rounded-full transition-all" style={{ width: k === i ? 22 : 8, background: k === i ? tokens.acc : 'var(--border)' }} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <ul className={`grid grid-cols-1 gap-3 ${colonnes}`}>
                    {c.points.map((t) => (
                        <li key={t} className="flex gap-3 rounded-xl border border-border bg-surface p-4 text-[14px] text-content-muted leading-snug">
                            <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-white" style={{ background: tokens.acc }} aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
                            <span>{t}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {agrandie && (
                <div className="fixed inset-0 z-[90] bg-black/80 flex flex-col" role="dialog" aria-modal="true" aria-label="Capture agrandie" onClick={() => setAgrandie(null)}>
                    <div className="flex justify-end p-4">
                        <button type="button" aria-label="Fermer" onClick={() => setAgrandie(null)} className="w-11 h-11 rounded-full bg-surface flex items-center justify-center"><X size={20} aria-hidden="true" /></button>
                    </div>
                    <div className="flex-1 overflow-auto px-4 pb-8" onClick={(e) => e.stopPropagation()}>
                        <div className="mx-auto max-w-[1500px] flex flex-wrap gap-4 justify-center">
                            {(c.duo ? c.duo.map((d) => ({ src: d.src, alt: d.alt })) : [{ src: c.src, alt: c.alt }]).map((im) => (
                                <img key={im.src} src={im.src} alt={im.alt} className={`h-auto rounded-xl shadow-2xl bg-surface ${c.duo ? 'max-w-[calc(50%-8px)]' : 'max-w-full'}`} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};
